import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Box,
} from "@mui/material";
import { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";

import { Document, Packer, Paragraph, TextRun } from "docx";
import { saveAs } from "file-saver";

const isLongAddress = (address = "") => address.length > 120;

const storeHeader = (store) => [
  new Paragraph({
    children: [
      new TextRun({
        text: store.storeName,
        bold: true,
        size: 40,
      }),
    ],
  }),
  new Paragraph({
    children: [
      new TextRun({
        text: store.phone,
        size: 24,
      }),
    ],
  }),
];

const createStoreSection = (store) => {
  const longAddress = isLongAddress(store.address);

  return {
    properties: {
      page: {
        size: {
          width: 5760, // 4"
          height: 3600, // 2.5"
        },
        margin: {
          top: 400,
          bottom: 200,
          left: 300,
          right: 300,
        },
      },
    },
    children: [
      // HEADER (Page 1)
      ...storeHeader(store),

      // ADDRESS
      new Paragraph({
        children: [new TextRun({ text: store.address, size: 24 })],
      }),

      // IF ADDRESS IS LONG → FORCE PAGE BREAK
      ...(longAddress
        ? [
            new Paragraph({
              pageBreakBefore: true,
            }),

            // HEADER AGAIN ON PAGE 2
            ...storeHeader(store),
          ]
        : []),

      // PRODUCT
      new Paragraph({
        children: [
          new TextRun({ text: "Product: ", bold: true, size: 24 }),
          new TextRun({ text: store.productType, size: 24 }),
        ],
      }),

      // SIZE
      new Paragraph({
        children: [
          new TextRun({ text: "Size: ", bold: true, size: 24 }),
          new TextRun({ text: store.size, size: 24 }),
        ],
      }),
    ],
  };
};

const DocModal = ({ open, handleClose, jobApprovals = [] }) => {
  const [filterStoreAddress, setFilterStoreAddress] = useState([]);
  useEffect(() => {
    if (!jobApprovals.length) return;

    const storeDetails = jobApprovals
      .filter(
        (approval) =>
          approval.adminStatus === 1 &&
          (approval.clientStatus === 1 || approval.clientStatus === 0),
      )
      .map((approval, index) => ({
        id: index + 1,
        storeName: approval?.job?.storeName || "",
        storeOwnerName: approval?.job?.storeOwnerName || "",
        address: approval?.job?.address || "",
        city: approval?.job?.city || "",
        state: approval?.job?.state || "",
        phone: approval?.job?.storeOwnerPhoneNumber || "",
        productType: approval?.product?.productType || "",
        size: `${approval?.product?.productHeight || ""}ft x ${
          approval?.product?.productWidth || ""
        }ft`,
      }));

    setFilterStoreAddress(storeDetails);
  }, [jobApprovals]);
  const handleDownload = async () => {
    const doc = new Document({
      sections: filterStoreAddress.map((storeAddress) =>
        createStoreSection(storeAddress),
      ),
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, "Store_Labels_4x2.5.docx");
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "1px solid #e0e0e0",
          position: "sticky",
          top: 0,
          background: "#fff",
          zIndex: 10,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          Store Label Preview
        </Typography>

        <Box>
          <Button
            startIcon={<DownloadIcon />}
            variant="contained"
            size="small"
            onClick={handleDownload}
            sx={{ mr: 1 }}
          >
            Download
          </Button>

          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* PREVIEW CONTENT */}
      <DialogContent>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(384px, 1fr))",
            gap: 2,
            justifyContent: "center",
          }}
        >
          {filterStoreAddress.flatMap((store) =>
            getPreviewPages(store).map((pageConfig, index) => (
              <LabelPreviewCard
                key={`${store.id}-${index}`}
                data={store}
                pageConfig={pageConfig}
              />
            )),
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DocModal;

const getPreviewPages = (store) => {
  const longAddress = isLongAddress(store.address);

  // PAGE 1
  const firstPage = {
    page: 1,
    showProduct: !longAddress,
    showSize: !longAddress,
  };

  // PAGE 2 (only if long)
  const secondPage = longAddress
    ? {
        page: 2,
        showProduct: true,
        showSize: true,
      }
    : null;

  return secondPage ? [firstPage, secondPage] : [firstPage];
};

const LabelPreviewCard = ({ data, pageConfig }) => {
  if (!data || !pageConfig) return null;

  const { page, showProduct, showSize } = pageConfig;

  return (
    <Box
      sx={{
        width: "384px", // 4"
        height: "240px", // 2.5"
        border: "1px dashed #ccc",
        padding: "12px",
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "space-between",
        backgroundColor: "#fff",
      }}
    >
      {/* LEFT CONTENT */}
      <Box sx={{ width: "100%" }}>
        {/* HEADER (ALWAYS ON EVERY PAGE) */}
        <Typography fontWeight={600} fontSize="18px" color="black">
          {data.storeName}
        </Typography>

        <Typography fontSize="14px" color="black">
          {data.phone}
        </Typography>

        {/* ADDRESS → ONLY ON PAGE 1 */}
        {page === 1 && (
          <>
            <Typography fontSize="14px" color="black">
              {data.address}
            </Typography>

            <Typography fontSize="14px" color="black">
              {data.city}, {data.state}
            </Typography>
          </>
        )}

        {/* PRODUCT → CONDITIONAL */}
        {showProduct && (
          <Typography fontSize="14px" color="black" mt={1}>
            <strong>Product:</strong> {data.productType}
          </Typography>
        )}

        {/* SIZE → CONDITIONAL */}
        {showSize && (
          <Typography fontSize="14px" color="black">
            <strong>Size:</strong> {data.size}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
